import React from 'react';
import PropTypes from 'prop-types';
import clone from 'clone';

import {connectAppEnv} from '../../../app/app-env';
import {frefFromRef, frefToRef} from '../../../lib/react/higher-order-components';
import {oeInterfaceManager} from '../../../react-oe/oe-interface';
import OEInterfaceAdapter from '../../../react-oe/oe-interface-adapter';
import OEStdModalController from '../../modals/oe-std-modal-controller';
import {OECustomRadioButton} from '../../elements/controls/oe-radio-button';
import {OEButtonControl} from '../../elements/controls/oe-controls';
import {OEScreenshotControllerConfigDecl, OEScreenshotControllerConfigFactory} from './oe-screenshot-controller-model';
import OEScreenshotTool, {OECornerAlignment} from '../../../lib/tools/oe-screenshot-tool';
import OEToolbox from '../../../lib/tools/oe-toolbox';
import OEClipboard from '../../../lib/tools/oe-clipboard';
import retardUpdate from '../../../lib/react/update-retarder';

export class OEScreenshotPreviewController extends React.PureComponent {

    constructor(props) {
        super(props);

        this.oe = oeInterfaceManager.getInterface(this.props.moduleId);

        this.attributeImage = true;
        this.attributionAlignment = OECornerAlignment.bottomLeft;
        this.attribution = this.createAttribution();

        this.state = {
            uiEnabled: false,
            attributeImage: this.attributeImage,
            attributeToClipboard: false,
            attributionAlignment: this.attributionAlignment,
            attribution: clone(this.attribution),
            fileFormat: 'png',
            strings: {
                title: '',
                attributeImage: '',
                attributeToClipboard: '',
                fileFormat: '',
                fileFormats: {
                    png: 'PNG',
                    jpg: 'JPEG'
                },
                saveBtn: ''
            },
            image: null
        };

        this.updateLanguage = this.updateLanguage.bind(this);

        this.onAttributeImageBtnPressed = this.onAttributeImageBtnPressed.bind(this);
        this.onAttributeToClipboardBtnPressed = this.onAttributeToClipboardBtnPressed.bind(this);
        this.onAttributionAlignmentBtnPressed = this.onAttributionAlignmentBtnPressed.bind(this);
        this.onSaveBtnPressed = this.onSaveBtnPressed.bind(this);
    }

    componentWillReceiveProps(nextProps) {
        let attributionChanged = !OEToolbox.shallowEqual(this.props.config.attribution, nextProps.config.attribution, true);

        let needsImageUpdate = false;

        if(attributionChanged)  needsImageUpdate = this.updateAttribution(nextProps, false);

        if(needsImageUpdate || this.props.image !== nextProps.image || this.props.config.attributionOptional !== nextProps.config.attributionOptional)    this.updateImage(nextProps);
    }

    onConnect()  {
        this.oe.sharedNotificationCenter.register(this.oe.NotificationName.languageChanged, this.updateLanguage);
    }

    onRelease()    {
        this.oe.sharedNotificationCenter.unregister(this.oe.NotificationName.languageChanged, this.updateLanguage);
    }

    updateLanguage(updateImage = true)   {
        let si = this.oe.sharedInterface;
        this.setState({strings: {
            title: si.getLocalizedStringEnc('screenshot_preview_view'),
            attributeImage: si.getLocalizedStringEnc('screenshot_preview_view_attribute_image'),
            attributeToClipboard: si.getLocalizedStringEnc('screenshot_preview_view_attribute_to_clipboard'),
            fileFormat: si.getLocalizedStringEnc('screenshot_view_file_format'),
            fileFormats: {
                png: si.getLocalizedStringEnc('screenshot_view_file_format_png'),
                jpg: si.getLocalizedStringEnc('screenshot_view_file_format_jpg')
            },
            saveBtn: si.getLocalizedStringEnc('screenshot_view_save_btn')
        }});

        this.updateAttribution(undefined, false);
        if(updateImage) this.updateImage();
    }

    updateUIState()   {
        this.setState({uiEnabled: this.oe.isReady()});
    }

    createAttribution(props) {
        props = props || this.props;

        if(!props.config.attribution)   return;

        let attribution = clone(props.config.attribution);

        if(!attribution.text && !attribution.textID && !attribution.imageURL)   return attribution;

        if(attribution.imageURL)    {
            attribution.text = undefined;
            attribution.textID = undefined;
            return attribution;
        }

        if(attribution.textID)    {
            attribution.text = this.oe.sharedInterface.getLocalizedStringEnc(attribution.textID);
            if(!attribution.text || attribution.text === '#null')   return;
            attribution.textID = undefined;
            return attribution;
        }

        return attribution;
    }

    updateAttribution(props, updateImage = true)    {
        let attribution = this.createAttribution(props);
        if(OEToolbox.shallowEqual(attribution, this.attribution, true)) return false;

        this.attribution = attribution;
        this.setState({attribution: clone(this.attribution)});
        if(updateImage) this.updateImage(props);
        return true;
    }

    createFinalImage(props)  {
        props = props || this.props;

        let attribution;
        if((this.attributeImage || !props.config.attributionOptional) && this.attribution)   {
            attribution = clone(this.attribution);
            attribution.alignment = attribution.alignment ? attribution.alignment : this.attributionAlignment;
        }

        return OEScreenshotTool.addAttribution(this.oe, OEScreenshotTool.copyCanvas(props.image), attribution);
    }

    updateImage(props)  {
        if(!this.oe.isReady())  return;

        let options = {
            imageFormat: this.state.fileFormat,
            quality: undefined
        };

        this.createFinalImage(props)
        .then(canvas => {
            const imageURL = OEScreenshotTool.canvasToDataURL(canvas, options);
            this.setState({image: imageURL});
        });
    }

    updateState(released)   {
        if(!this.oe.isReady() || released === true)   {
            this.setState({ uiEnabled: false });
            return;
        }
        
        retardUpdate(this, () => {
            this.updateLanguage(false);
            this.updateAttribution(undefined, false);
            this.updateUIState();
            this.updateImage();
        });
    }

    render() {
        let hasAttribution = this.state.attribution;
        let hasAttributionImage = this.state.attribution && this.state.attribution.imageURL;
        let hasFixedAttributionAlignment = this.state.attribution && this.state.attribution.alignment;

        return (
            <React.Fragment>
                <OEInterfaceAdapter moduleId={this.props.moduleId} receiver={this}/>
                <OEStdModalController
                    moduleId={this.props.moduleId}
                    title={this.state.strings.title}
                    className="simple-header flow-nav-modal no-navigation-controller center fit-content screenshot-preview-controller"
                    isOpen={this.props.isOpen}
                    hasCloseBtn={true}
                    onClosed={this.props.onClosed}
                    onToggle={this.props.onToggle}
                >

                    <div className="image-container">
                        <img src={this.state.image}/>
                    </div>

                    <div className="btn-bar">
                        <div className="left">
                            {!hasAttribution ? null : 
                                <div className="attribute-image-control">

                                    {!this.props.config.attributionOptional ? null :
                                        <React.Fragment>
                                            <OECustomRadioButton
                                                disabled={!this.state.uiEnabled}
                                                checked={this.state.attributeImage}
                                                onPressed={this.onAttributeImageBtnPressed}
                                            />
                                            <span className="label">{this.state.strings.attributeImage}</span>
                                        </React.Fragment>
                                    }

                                    {hasFixedAttributionAlignment ? null : 
                                        <div className="alignment">
                                            <OECustomRadioButton
                                                disabled={!this.state.uiEnabled}
                                                checked={this.state.attributionAlignment === OECornerAlignment.topLeft}
                                                onPressed={this.onAttributionAlignmentBtnPressed}
                                                userData={OECornerAlignment.topLeft}
                                            />
                                            <OECustomRadioButton
                                                disabled={!this.state.uiEnabled}
                                                checked={this.state.attributionAlignment === OECornerAlignment.topRight}
                                                onPressed={this.onAttributionAlignmentBtnPressed}
                                                userData={OECornerAlignment.topRight}
                                            />
                                            <OECustomRadioButton
                                                disabled={!this.state.uiEnabled}
                                                checked={this.state.attributionAlignment === OECornerAlignment.bottomLeft}
                                                onPressed={this.onAttributionAlignmentBtnPressed}
                                                userData={OECornerAlignment.bottomLeft}
                                            />
                                            <OECustomRadioButton
                                                disabled={!this.state.uiEnabled}
                                                checked={this.state.attributionAlignment === OECornerAlignment.bottomRight}
                                                onPressed={this.onAttributionAlignmentBtnPressed}
                                                userData={OECornerAlignment.bottomRight}
                                            />
                                        </div>
                                    }
                                </div>
                            }

                            {!hasAttribution || hasAttributionImage || !this.props.config.copyToClipboardOption ? null :
                                <div className="attribute-image-control">
                                    <OECustomRadioButton
                                        disabled={!this.state.uiEnabled}
                                        checked={this.state.attributeToClipboard}
                                        onPressed={this.onAttributeToClipboardBtnPressed}
                                    />
                                    <span className="label">{this.state.strings.attributeToClipboard}</span>
                                </div>
                            }

                        </div>
                        <OEButtonControl className="save-btn" disabled={!this.state.uiEnabled} onPressed={this.onSaveBtnPressed}>{this.state.strings.saveBtn}</OEButtonControl>
                    </div>

                </OEStdModalController>
            </React.Fragment>
        );
    }

    onAttributeImageBtnPressed()    {
        this.attributeImage = !this.attributeImage;
        this.setState({attributeImage: this.attributeImage});
        this.updateImage();
    }

    onAttributeToClipboardBtnPressed()  {
        this.setState(state => {
            let state_ = clone(state);
            state_.attributeToClipboard = !state.attributeToClipboard;
            return state_;
        });
    }

    onAttributionAlignmentBtnPressed(sender)    {
        if(this.attributionAlignment === sender.props.userData) return;
        this.attributionAlignment = sender.props.userData;
        this.setState({attributionAlignment: this.attributionAlignment});
        this.updateImage();
    }

    onSaveBtnPressed()  {
        if(!this.state.image) return;
        let filename = OEToolbox.lowerCamelCaseToLowercaseUnderscore(this.props.appTarget, '-') + '-screenshot.' + (this.state.fileFormat === 'png' ? 'png' : 'jpg');

        if(this.state.attributeToClipboard && this.attribution && this.attribution.text)    {
            OEClipboard.copyText(this.attribution.text).then(null, () => {});
        }
        
        OEScreenshotTool.safe(this.state.image, filename);

        if(this.props.onSaveBtnPressed) this.props.onSaveBtnPressed(this);
    }
}

OEScreenshotPreviewController.defaultProps = {
    moduleId: '',
    appTarget: '',
    config: OEScreenshotControllerConfigFactory.buildStdConfig()
};

OEScreenshotPreviewController.propTypes = {
    moduleId: PropTypes.string,
    appTarget: PropTypes.string,
    config: OEScreenshotControllerConfigDecl,
    isOpen: PropTypes.bool,
    onClosed: PropTypes.func,
    onToggle: PropTypes.func,
    onSaveBtnPressed: PropTypes.func
};

export default frefFromRef(connectAppEnv((env) => {
    return {
        appComponent: env.component,
        appTarget: env.config.target,
    }
})(frefToRef(OEScreenshotPreviewController)));